/* eslint-disable @typescript-eslint/no-unused-vars */
import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
    financialDiscountExcel,
    getDiscountDetail,
    updateDiscount,
} from "Services/Reporting";
import {
    Button,
    PageTemplate,
    ResponseHandler,
    Table,
    Toast,
} from "components";
import { Form } from "react-bootstrap";
import { useToast } from "hooks/useToast";
import styles from "./style.module.scss";
import { ReactComponent as Download } from "assets/icons/cloud-download.svg";
import { GetDiscountItem } from "models/Report";

const Discount = () => {
    const params = {
        trashed: 1,
        page: 1,
        page_size: 1000,
    };
    const [discountData, setDiscountData] = useState<GetDiscountItem[]>([]);
    const [editedData, setEditedData] = useState<GetDiscountItem[]>([]);
    const [_, setValue] = useState("");
    const [submitStatus, setSubmitStatus] = useState(false);
    const activeName = useRef("");
    const { toast, setToast, reset } = useToast();

    const { vendorID, periodID } = useParams<{
        vendorID: string;
        periodID: string;
    }>();

    const { isFetching, isError } = useQuery(
        ["getDiscount", periodID, vendorID],
        () => getDiscountDetail({ periodID, vendorID, params }),
        {
            onSuccess: (res) => {
                const { period_status, items } = res?.data?.data;
                setDiscountData(items);
                setSubmitStatus(period_status !== 1);
            },
        }
    );

    const { mutate: mutateExportExcel } = useMutation(
        ["financial-sale-excel"],
        financialDiscountExcel,
        {
            onSuccess: (res) => {
                const link = document.createElement("a");
                link.href = res.data;
                link.setAttribute("download", "financial-sale-excel");
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
            },
        }
    );

    const { mutate: updateDiscountMutation, isLoading: isLoadingUpdate } =
        useMutation(["update-discount"], updateDiscount, {
            onSuccess: () =>
                setToast({
                    status: "success",
                    show: true,
                    message: "تخفیف با موفقیت اعمال شد.",
                }),
        });

    const handleInputChange = (e: any, props: any) => {
        const { value } = e.target;
        const intValue = value.replace(/\D/g, "");
        const index = discountData.findIndex(
            (el: any) => el.id === props.row.original?.id
        );
        activeName.current = e.target.name;
        const discountTemp: GetDiscountItem[] = discountData;
        discountTemp[index].amount = intValue;
        setDiscountData(discountTemp);
        updateEditedData(e, props);
        setValue(intValue);
    };

    const updateEditedData = (e: any, props: any) => {
        const editedIndex = editedData?.findIndex(
            (item: any) => item.id === props.row.original.id
        );
        if (editedIndex !== -1) {
            const temp = editedData;
            temp[editedIndex].amount = e.target.value;
            temp[editedIndex].has_changed = true;
            setEditedData(temp);
        } else {
            const { has_changed, ...row } = props.row.original;
            const newRow = { has_changed: true, ...row };
            setEditedData((prev: any) => [...prev, newRow]);
        }
    };

    const COLUMNS = [
        {
            Header: "شعبه",
            accessor: "branch_title",
        },
        {
            Header: "تخفیفات کیچن (ریال)",
            accessor: "amount",
            Cell: (props: any) => renderAmountInput(props),
        },
    ];

    const renderAmountInput = (props: any) => {
        const editedIndex = editedData?.findIndex(
            (item: any) => item.id === props.row.original.id
        );
        const InputChanged = editedData[editedIndex]?.has_changed;
        const name = "amount" + props.row.original.id;
        const hasChanged = props.row.original.has_changed || InputChanged;

        return (
            <Form.Control
                className={`${styles.discountInputForm} ${
                    !hasChanged && styles.hasChanged
                }`}
                type="text"
                name={name}
                autoFocus={name === activeName.current}
                value={props.row.original.amount}
                onChange={(e) => {
                    handleInputChange(e, props);
                }}
            />
        );
    };

    const validateSubmit = () => {
        const emptyField = editedData.filter(
            (item: GetDiscountItem) => item?.amount === ""
        );
        reset();
        if (editedData.length === 0) {
            setTimeout(
                () =>
                    setToast({
                        status: "danger",
                        show: true,
                        message: "تغییرات اعمال نشده است.",
                    }),
                500
            );
            return false;
        }

        if (emptyField?.length) {
            setTimeout(
                () =>
                    setToast({
                        status: "danger",
                        show: true,
                        message: "لطفا فیلدهای خالی را پرکنید",
                    }),
                500
            );
            return false;
        } else {
            return true;
        }
    };

    const handleSubmit = () => {
        activeName.current = "";
        validateSubmit() && updateDiscountMutation(editedData);
    };

    return (
        <ResponseHandler loading={isFetching} error={isError}>
            <PageTemplate
                isNewBackground
                groupName="گزارشات"
                title={"دوره مالی"}
                listOfTitles={["تخفیفات"]}
            >
                <div className={styles.discountExportExcalBtn}>
                    <Button
                        btnText={"خروجی اکسل"}
                        className="is-green bordered half-height"
                        handleClick={() =>
                            mutateExportExcel({ periodID, vendorID })
                        }
                        btnIcon={<Download className="mr-2" />}
                    />
                </div>
                <Toast
                    className={toast.status}
                    isShow={toast.show}
                    title={toast.message}
                />
                <div className={styles.discountTableContainer}>
                    <Table
                        columns={COLUMNS}
                        data={discountData}
                        className={`${styles.financialTable} ${styles.discountCursor}`}
                    />
                </div>

                <div className={styles.SubmitDiscountBtn}>
                    <Button
                        btnText="ثبت"
                        className="solid is-green"
                        handleClick={handleSubmit}
                        disabled={submitStatus}
                        loading={isLoadingUpdate}
                    />
                </div>
            </PageTemplate>
        </ResponseHandler>
    );
};

export default Discount;
